/**
 * Safely extracts a human-readable error message from an Axios error object
 * or any other error thrown during API calls.
 * 
 * @param error The error object to parse
 * @param fallback A default message if no specific error can be extracted
 * @returns {string} The extracted error message
 */
export function extractErrorMessage(error: any, fallback: string = 'Something went wrong'): string {
  if (!error) return fallback;

  // Check if it's an Axios error with a response
  if (error.response?.data) {
    const data = error.response.data;

    if (typeof data === 'string' && data.trim()) return data;
    // Check for common backend error shapes
    if (typeof data.error?.message === 'string') return data.error.message;  // { error: { message } }
    if (typeof data.message === 'string') return data.message;
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail)) {
        // FastAPI validation errors (422) often return an array of detail objects
        return data.detail.map((d: any) => (typeof d === 'string' ? d : d.msg || d.message)).join(', ') || fallback;
    }
  }

  // Fallback to error.message if available (e.g. network errors), ignoring generic Axios status code errors
  if (error.message && typeof error.message === 'string' && !/status code \d+/i.test(error.message)) {
    return error.message;
  }

  return fallback;
}
